/*
 * Copyright (c) 2020. American Express Company
 */

import { InvestmentHoldingModel, InvestmentHoldingType, Ticker } from 'src/types/investment-holding.model';
import BWDataService from './bw-data.service';
import DateUtil from 'src/utils/date';
import MixpanelService from './mixpanel.service';

export default class InvestmentHoldingService extends BWDataService {
    public static async postInvestmentHolding(
        investmentHolding: InvestmentHoldingModel
    ): Promise<InvestmentHoldingModel> {
        if (investmentHolding.type === InvestmentHoldingType.RealEstate) {
            return await InvestmentHoldingService.createRealEstate(investmentHolding);
        }

        MixpanelService.track('Add Asset', {
            Type: investmentHolding.type
        });

        return await this.post('/investmentholdings', {
            type: investmentHolding.type,
            subtype: investmentHolding.subtype,
            name: investmentHolding.name,
            amount: investmentHolding.amount ? Number(investmentHolding.amount) : undefined,
            ticker: investmentHolding.ticker?.length > 0 ? investmentHolding.ticker : undefined,
            symbol: investmentHolding.symbol,
            quantity: investmentHolding.quantity ? Number(investmentHolding.quantity) : undefined,
            price: investmentHolding.price ? Number(investmentHolding.price) : undefined,
            interest: investmentHolding.interest ? Number(investmentHolding.interest) : undefined,
            attributes: investmentHolding.attributes
        });
    }

    public static async createRealEstate(investmentHolding: InvestmentHoldingModel): Promise<InvestmentHoldingModel> {
        const attributes: { [key: string]: any } = investmentHolding.attributes;

        MixpanelService.track('Add Asset', {
            Type: 'real_estate'
        });

        return await this.post('/investmentrealestate', {
            address: attributes.address,
            rate: investmentHolding.interest ? Number(investmentHolding.interest) : undefined,
            amount: investmentHolding.amount ? Number(investmentHolding.amount) : undefined,
            mortgageType: attributes.mortgageType ? attributes.mortgageType : 'mortgage_none',
            mortgageAmount: attributes.mortgageAmount ? Number(attributes.mortgageAmount) : undefined,
            mortgageDate: DateUtil.dateify(attributes.mortgageDate),
            mortgageInterest: attributes.mortgageInterest ? Number(attributes.mortgageInterest) : undefined,
            mortgagePayment: attributes.mortgagePayment ? Number(attributes.mortgagePayment) : undefined
        });
    }

    public static async getInvestmentHoldings(): Promise<InvestmentHoldingModel[]> {
        return await this.get('/investmentholdings');
    }

    public static async deleteInvestmentHolding(id: number): Promise<number> {
        return await this.delete(`/investmentholdings/${id}`);
    }

    public static async updateInvestmentHolding(
        investmentHolding: InvestmentHoldingModel
    ): Promise<InvestmentHoldingModel> {
        return await this.put(`/investmentholdings/${investmentHolding.id}`, {
            type: investmentHolding.type,
            name: investmentHolding.name,
            amount: Number(investmentHolding.amount),
            ticker: investmentHolding.ticker?.length > 0 ? investmentHolding.ticker : undefined,
            symbol: investmentHolding.symbol,
            quantity: investmentHolding.quantity ? Number(investmentHolding.quantity) : 0,
            price: investmentHolding.price ? Number(investmentHolding.price) : 0,
            interest: investmentHolding.interest,
            attributes: investmentHolding.attributes
        });
    }

    public static async getTicker(ticker: string): Promise<Ticker> {
        return await this.get('/investmentsecurities/one/' + ticker);
    }

    /** Create investment holding from formik values */
    public static async createInvestmentHoldingFromFormik(values: any, type?: string, subtype?: string): Promise<void> {
        const investmentHolding = {
            type,
            subtype,
            name: values.name,
            amount: values.amount,
            interest: values.interest,
            ticker: values.ticker,
            price: values.price,
            quantity: values.quantity
        } as InvestmentHoldingModel;
        await InvestmentHoldingService.postInvestmentHolding(investmentHolding);
    }

    /** Update investment holding from formik values */
    public static async updateInvestmentHoldingFromFormik(
        values: any,
        investmentHolding: InvestmentHoldingModel
    ): Promise<void> {
        investmentHolding.name = values.name;
        investmentHolding.amount = values.amount;
        investmentHolding.interest = values.interest;
        await InvestmentHoldingService.updateInvestmentHolding(investmentHolding);
    }
}
